import React, { useState, useMemo, useCallback, useEffect } from "react";
import ReactDOM from "react-dom";
import LanguageContext from "../helper/LanguageContext";
import { decryptMessage } from "../utils/encryptDecryptFunction";
import { BASE_URL } from "../helper/constants";
import "./PollMessage.css";

const DEFAULT_AVATAR = process.env.PUBLIC_URL + "/assets/img/profile.png";

function PollVotesModal({ pollData, votes, groupMembers, accentColor, onClose, strings }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const memberMap = useMemo(() => {
    const map = {};
    if (Array.isArray(groupMembers)) {
      groupMembers.forEach((m) => { map[m.id] = m; });
    }
    return map;
  }, [groupMembers]);

  const optionsWithVoters = useMemo(() => {
    if (!pollData?.options) return [];
    return pollData.options.map((option, i) => {
      const voterIds = votes[String(i)] || [];
      const voters = voterIds.map((uid) => {
        const member = memberMap[uid];
        return {
          id: uid,
          name: member?.fullName || member?.name || uid.substring(0, 8),
          avatar: member?.avatar ? BASE_URL + member.avatar : DEFAULT_AVATAR,
        };
      });
      return { option, voters, count: voterIds.length };
    });
  }, [pollData, votes, memberMap]);

  const totalVotes = useMemo(() => {
    const set = new Set();
    Object.values(votes).forEach((arr) => {
      if (Array.isArray(arr)) arr.forEach((uid) => set.add(uid));
    });
    return set.size;
  }, [votes]);

  const modal = (
    <div className="poll-votes-modal-overlay" onClick={onClose}>
      <div className="poll-votes-modal" onClick={(e) => e.stopPropagation()}>
        <div className="poll-votes-modal-header">
          <span
            className="poll-votes-modal-close material-symbols-outlined"
            onClick={onClose}
          >
            close
          </span>
          <span className="poll-votes-modal-title">
            {strings.poll_votes || "Poll votes"}
          </span>
          <span className="poll-votes-modal-header-spacer" />
        </div>

        <div className="poll-votes-modal-question">{pollData.question}</div>

        <div className="poll-votes-modal-meta">
          {totalVotes} {totalVotes === 1 ? (strings.poll_vote_singular || "vote") : (strings.poll_vote_plural || "votes")}
        </div>

        <div className="poll-votes-modal-body">
          {optionsWithVoters.map((item, i) => (
            <div key={i} className="poll-votes-modal-option">
              <div className="poll-votes-modal-option-header">
                <span className="poll-votes-modal-option-text">{item.option}</span>
                {item.count > 0 && (
                  <span className={`poll-votes-modal-option-badge poll-message__badge--${accentColor === "var(--kjuup-internal)" ? "internal" : "accent"}`}>
                    {item.count}
                  </span>
                )}
              </div>
              {item.voters.length > 0 ? (
                <div className="poll-votes-modal-voters">
                  {item.voters.map((voter) => (
                    <div key={voter.id} className="poll-votes-modal-voter">
                      <img
                        src={voter.avatar}
                        alt=""
                        className="poll-votes-modal-voter-avatar"
                        onError={(e) => { e.target.src = DEFAULT_AVATAR; }}
                      />
                      <span className="poll-votes-modal-voter-name">{voter.name}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="poll-votes-modal-no-votes">
                  {strings.poll_no_votes_yet || "No votes yet"}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
}

/**
 * Poll bubble (mobile ProMediaBlocks.ProPoll): "Umfrage" label and the question; 42 tall
 * option rows filled to their share of the votes (accent-soft-2 with an accent border for
 * your vote), a round check and the count; the footer "{n} Stimmen · Du hast abgestimmt"
 * opens the votes list (PollVotesModal) once there are votes. Same props and voting calls.
 */
export default function PollMessage({ chat, userId, isInternal, onVote, secretKey, groupMembers }) {
  const { strings, isGerm } = React.useContext(LanguageContext);
  const [localVotes, setLocalVotes] = useState(null);
  const [showVotesModal, setShowVotesModal] = useState(false);

  useEffect(() => {
    setLocalVotes(null);
  }, [chat.message]);

  const pollData = useMemo(() => {
    try {
      const raw = typeof chat.message === "string" ? chat.message : null;
      if (!raw) return typeof chat.message === "object" ? chat.message : null;

      try {
        const parsed = JSON.parse(raw);
        if (parsed && parsed.question) return parsed;
      } catch {}

      if (secretKey) {
        try {
          const decrypted = decryptMessage(raw, secretKey);
          if (decrypted) {
            const parsed = JSON.parse(decrypted);
            if (parsed && parsed.question) return parsed;
          }
        } catch {}
      }

      return null;
    } catch {
      return null;
    }
  }, [chat.message, secretKey]);

  const votes = localVotes || (pollData ? pollData.votes : null) || {};
  const allowMultiple = (pollData && pollData.allowMultiple) || false;
  const accentColor = isInternal ? "var(--kjuup-internal)" : "var(--kjuup-accent)";

  const totalVoters = useMemo(() => {
    const voterSet = new Set();
    Object.values(votes).forEach((arr) => {
      if (Array.isArray(arr)) arr.forEach((uid) => voterSet.add(uid));
    });
    return voterSet.size;
  }, [votes]);


  const handleViewVotes = useCallback((e) => {
    e.stopPropagation();
    setShowVotesModal(true);
  }, []);

  if (!pollData || !pollData.question) return null;

  const handleVote = (optionIndex) => {
    const key = String(optionIndex);
    const nextVotes = { ...votes };

    Object.keys(nextVotes).forEach((k) => {
      if (!Array.isArray(nextVotes[k])) nextVotes[k] = [];
    });

    if (!nextVotes[key]) nextVotes[key] = [];

    const alreadyVoted = nextVotes[key].includes(userId);

    if (alreadyVoted) {
      nextVotes[key] = nextVotes[key].filter((id) => id !== userId);
    } else {
      if (!allowMultiple) {
        Object.keys(nextVotes).forEach((k) => {
          nextVotes[k] = nextVotes[k].filter((id) => id !== userId);
        });
      }
      nextVotes[key] = [...nextVotes[key], userId];
    }

    setLocalVotes(nextVotes);
    if (onVote) onVote(chat.id, nextVotes, pollData);
  };

  const hasUserVoted = Object.values(votes).some(
    (arr) => Array.isArray(arr) && arr.includes(userId)
  );
  const optionCounts = pollData.options.map((_, i) => (votes[String(i)] || []).length);
  const totalVotes = optionCounts.reduce((sum, n) => sum + n, 0);
  const votesLabel = `${totalVotes} ${totalVotes === 1 ? (strings.poll_vote_singular || "vote") : (strings.poll_vote_plural || "votes")}`;
  const hint = allowMultiple
    ? (strings.poll_multiple_allowed || "Select one or more options")
    : (strings.poll_select_option || "Select one option");
  const status = hasUserVoted ? (strings.chat_poll_you_voted || (isGerm ? "Du hast abgestimmt" : "You voted")) : hint;
  const canViewVotes = totalVoters > 0;
  const viewVotesLabel = strings.poll_view_votes || "View votes";

  // Divs, not spans: chat bubbles force their own font size and colour onto spans.
  return (
    <div className={`kjc-poll${isInternal ? " kjc-poll--internal" : ""}`}>
      <div className="kjc-poll__label">
        <div className="material-symbols-outlined kjc-poll__label-icon">poll</div>
        <div className="kjc-poll__label-text">{strings.create_poll || (isGerm ? "Umfrage" : "Poll")}</div>
      </div>
      <div className="kjc-poll__question">{pollData.question}</div>
      <div className="kjc-poll__options" role={allowMultiple ? "group" : "radiogroup"} aria-label={pollData.question}>
        {pollData.options.map((option, i) => {
          const count = optionCounts[i];
          const isSelected = (votes[String(i)] || []).includes(userId);
          const pct = totalVotes > 0 ? Math.round((count / totalVotes) * 100) : 0;
          return (
            <div
              key={i}
              role={allowMultiple ? "checkbox" : "radio"}
              aria-checked={isSelected}
              tabIndex={0}
              className={`kjc-poll__opt${isSelected ? " is-selected" : ""}`}
              onClick={() => handleVote(i)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleVote(i);
                }
              }}
            >
              <div className="kjc-poll__fill" style={{ "--kjc-poll-pct": `${pct}%` }} />
              <div className="kjc-poll__check">
                {isSelected ? <div className="material-symbols-outlined kjc-poll__check-icon">check</div> : null}
              </div>
              <div className="kjc-poll__opt-text">{option}</div>
              <div className="kjc-poll__count">{count}</div>
            </div>
          );
        })}
      </div>
      <div
        className={`kjc-poll__foot${canViewVotes ? " is-pressable" : ""}`}
        role={canViewVotes ? "button" : undefined}
        tabIndex={canViewVotes ? 0 : undefined}
        title={canViewVotes ? viewVotesLabel : undefined}
        aria-label={canViewVotes ? `${votesLabel} · ${status}. ${viewVotesLabel}` : undefined}
        onClick={canViewVotes ? handleViewVotes : undefined}
        onKeyDown={canViewVotes ? (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleViewVotes(e);
          }
        } : undefined}
      >
        <div className="kjc-poll__total">{status ? `${votesLabel} · ${status}` : votesLabel}</div>
        {canViewVotes ? <div className="material-symbols-outlined kjc-poll__foot-icon">chevron_right</div> : null}
      </div>

      {showVotesModal && (
        <PollVotesModal
          pollData={pollData}
          votes={votes}
          groupMembers={groupMembers}
          accentColor={accentColor}
          onClose={() => setShowVotesModal(false)}
          strings={strings}
        />
      )}
    </div>
  );
}
